import { RefObject, useEffect, useState } from 'react'

type Options = {
  /** Once true, stop observing and stay true (good for lazy mounting). */
  once?: boolean
  threshold?: number
  rootMargin?: string
}

/**
 * Returns whether the referenced element is (or has been) in the viewport.
 * Mirrors the IntersectionObserver pattern used by `use-footer-visible`.
 */
export const useInView = (
  ref: RefObject<Element | null>,
  { once = false, threshold = 0.1, rootMargin = '0px' }: Options = {}
): boolean => {
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          if (once) observer.disconnect()
        } else if (!once) {
          setInView(false)
        }
      },
      { threshold, rootMargin }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [ref, once, threshold, rootMargin])

  return inView
}
